import React, { useState } from 'react';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { setDoc, doc } from 'firebase/firestore';
import { auth, db } from '../firebaseConfig'; 
import logo from '../img/logo.png';
import { useNavigate } from 'react-router-dom'; 
import { useTranslation } from 'react-i18next';


// Styled Components
const CenteredContainer = styled(Container)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100vh',
});

const PaperContainer = styled(Paper)(({ theme }) => ({
  margin: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(4),
  borderRadius: '20px',
}));

const LogoImage = styled('img')(({ theme }) => ({
  width: '350px',
  height: 'auto',
}));

const AvatarIcon = styled(Avatar)(({ theme }) => ({
  margin: theme.spacing(1),
  backgroundColor: theme.palette.secondary.main,
}));

const Form = styled('form')(({ theme }) => ({
  width: '100%',
  marginTop: theme.spacing(1),
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(3, 0, 2),
}));

export default function AdminRegister() {

  const { t } = useTranslation();
  const navigate = useNavigate(); 

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
  });

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    const { fullName, email, password } = formData;

    try {
      // Register admin in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const userId = userCredential.user.uid;

      // Save admin details in Firestore
      await setDoc(doc(db, 'admin', userId), {
        fullName,
        email,
        role: 'admin', 
        uid: userId,
      });

      setSuccess(t('Admin registered successfully!'));
      navigate('/06071966/AdminAllStudents'); 
      setFormData({ fullName: '', email: '', password: '' }); 
    } catch (err) {
      console.error('Error registering admin:', err);
  
      // Handle common Firebase Auth errors
      switch (err.code) {
        case 'auth/email-already-in-use':
          setError(t('هذا البريد الإلكتروني مسجل بالفعل. يرجى استخدام بريد إلكتروني آخر.'));
          break;
        case 'auth/invalid-email':
          setError(t('عنوان البريد الإلكتروني غير صالح. يرجى إدخال بريد إلكتروني صحيح.'));
          break;
        case 'auth/weak-password':
          setError(t('كلمة المرور ضعيفة جدًا. استخدم 6 أحرف على الأقل.'));
          break;
        case 'auth/missing-email':
          setError(t('يرجى تقديم عنوان بريد إلكتروني.'));
          break;
        case 'auth/internal-error':
          setError(t('حدث خطأ داخلي. يرجى المحاولة مرة أخرى.'));
          break;
        default:
          setError(err.message || t('An unexpected error occurred. Please try again.'));
      }
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f8f8f8',
      }}
    >
      <CssBaseline />
      <CenteredContainer component="main" maxWidth="sm">
        <LogoImage src={logo} alt={t('Logo')} />
        <PaperContainer>
          <AvatarIcon>
            <LockOutlinedIcon />
          </AvatarIcon>
          <Typography component="h1" variant="h5">
            {t('Administration Staff Register Page')}
          </Typography>
          
          <Form noValidate onSubmit={handleSubmit}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault(); 
              handleSubmit(e); 
            }
          }}>
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="fullname"
              label={t('Full name')}
              name="fullName"
              autoComplete="name"
              autoFocus
              value={formData.fullName}
              onChange={handleChange}
            />
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="email"
              label={t('Email Address')}
              name="email"
              autoComplete="email"
              value={formData.email}
              onChange={handleChange}
            />
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="password"
              label={t('Password')}
              type="password"
              id="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleChange}
            />

              {error && <Typography color="error">{error}</Typography>}
              {success && <Typography color="success">{success}</Typography>}


            <SubmitButton type="submit" fullWidth variant="contained" color="primary">
              {t('Register')}
            </SubmitButton>
            <Container>
              <Link href="/06071966/login" variant="body2">
                {t('Back to Login page !!')}
              </Link>
            </Container>
          </Form>
        </PaperContainer>
      </CenteredContainer>
    </Box>
  );
}
